<template>
<div class="container-fluid">
  <div class="row">
    <div class="col-12">
            
      <div class="d-flex align-items-center">
        <h1 class="flex-grow-1" data-test="pageTitle">All Physicians</h1>
        <span
          v-if="perms.can_create_physician"
          v-tooltip.bottom="'Add Physician'"
          class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none"
          data-toggle="modal" data-target="#physicianCreateModal">
          <i class="far fa-plus-circle text-xl"></i>
        </span>
      </div>

      <div class="d-flex justify-content-between">
        <filter-bar
          :filter_type="['specialty', 'group']"
          @filter-set="setFilter($event)"
          @filter-group-select="selectGroupFilter($event)"
          @filter-specialty-select="selectSpecialtyFilter($event)"
          @filter-reset="resetAllFilters()"
        />

        <div class="dropdown">
          <button
            type="button"
            class="btn btn-block leading-none dropdown-toggle"
            :class="selectedPhysicians.length < 1 ? 'btn-light' : 'btn-primary'"
            :disabled="selectedPhysicians.length < 1"
            data-toggle="dropdown"
            data-test="addToSelectorButton">
            Add to...
          </button>
          <div class="dropdown-menu dropdown-menu-right py-0">
            <span
              class="dropdown-item d-flex align-items-baseline"
              @click="setRouteVisits()"
              data-toggle="modal" data-target="#routeCreateModal" data-test="addToRouteButton"
            >
              <div class="mr-3">
                <i class="fal fa-route mr-1"></i> New Route
              </div>
              <span class="badge badge-primary">{{ routeStops.length }}</span>
            </span>
            <span
              class="dropdown-item d-flex align-items-baseline"
              @click="setCampaignVisits()"
              data-toggle="modal" data-target="#campaignCreateModal" data-test="addToCampaignButton"
            >
              <div class="mr-3">
                <i class="fal fa-fire-alt mr-1"></i> New Campaign
              </div>
              <span class="badge badge-primary">{{ selectedPhysicians.length }}</span>
            </span>
          </div>
        </div>
      </div>

    <div class="card leading-tight">
      <vuetable ref="vuetable"
        api-url="/api/physicians/physicians_paged"
        :fields="fields"
        data-path="data"
        pagination-path=""
        :css="css.table"
        :sort-order="sortOrder"
        :sort-params="getSortParam"
        :multi-sort="false"
        :append-params="moreParams"
        :no-data-template="emptyText"
        @vuetable:load-success="onDataLoaded"
        @vuetable:pagination-data="onPaginationData">

        <div slot="last_name" slot-scope="props" class="d-flex align-items-center">
          <input class="mr-1" type="checkbox" v-model="selectedPhysicians" :value="props.rowData.id" >

          <div class="rounded-circle circle-cropped avatar-sm mr-2 font-weight-bold text-sm text-center">
            <img v-if="props.rowData.photo_path" :src="props.rowData.photo_url" class="img-fluid">
            <div v-else class="bg-gray-200 text-gray-600 border">{{ props.rowData.initials }}</div>
          </div>

          <div>
            <div>
              <router-link :to="{ name: 'physicians.show', params:{ id:props.rowData.id } }" data-test="viewPhysicianLink">
                {{ props.rowData.display_name }}
              </router-link>

              <i v-if="props.rowData.system_links && props.rowData.system_links.length > 0 && props.rowData.system_links[0].system_id != null" 
                class="far fa-link text-sm text-muted ml-1"
                v-tooltip.bottom="'linked to EMR'" />

              <i v-if="!props.rowData.location_primary || (props.rowData.location_primary && !props.rowData.location_primary.latitude)"
                class="far fa-map-marker-alt-slash text-sm text-gray-500 ml-1"
                v-tooltip.bottom="'No location'"
                data-test="noLocation" />        
            </div>
            <div>
              <span
                v-for="specialty in props.rowData.specialties"
                class="badge bg-teal-200 text-teal-700 mr-1"
                v-html="specialty.label"
              />
            </div>

              <div v-if="showPhysicianGroups && props.rowData.groups" class="text-sm mt-2 text-gray-700">
                <i class="fal fa-users-medical fa-fw"></i> <span v-for="(group, index) in props.rowData.groups">{{ index != 0 ? ', ' : ''}} {{ group.display_name }}</span>
              </div>
          </div>
        </div>

        <template slot="location_primary" slot-scope="props" v-if="props.rowData.location_primary">
          {{ props.rowData.location_primary.city }}{{ props.rowData.location_primary.state ? ', ' + props.rowData.location_primary.state : '' }}
        </template>

        <template slot="last_checkin_id" slot-scope="props" v-if="props.rowData.last_checkin_id">
          <span class="badge" :class="props.rowData.last_checkin.checkin_type.css_class_name">{{ props.rowData.last_checkin.checkin_type.label }}</span>
          <div class="mt-1 text-sm text-gray-600 leading-none">{{ moment(props.rowData.last_checkin.created_at).format('MMM D, YYYY') }}</div>          
        </template>

        <template slot="created_at" slot-scope="props">
          <div class="text-gray-600 text-sm">
            {{ moment(props.rowData.created_at).format('MMM D, YYYY') }}
            <div v-if="props.rowData.user" class="text-sm text-gray-600 mt-1 leading-none">by {{ props.rowData.user.display_name }}</div>
            <div v-else class="mt-1 leading-none"><em>by admin</em></div>
          </div>
        </template>

        <template slot="actions" slot-scope="props">
          <div class="drop-down dropleft">
            <a href="#" role="button" id="dropdownMenuLink" data-toggle="dropdown">
                <i class="far fa-ellipsis-h text-xl"></i>
            </a>
            <div class="dropdown-menu py-0" aria-labelledby="dropdownMenuLink">
              <router-link :to="{ name: 'physicians.show', params:{ id:props.rowData.id } }" class="dropdown-item" data-test="providerProfileLink">View Profile</router-link>
              <span class="dropdown-item" @click="buildCheckinModal(props.rowData.id, props.rowData.display_name)" data-toggle="modal" data-target="#myModal">Add Check-in</span>
            </div>
          </div>
        </template>        
      </vuetable>
    </div>

    <div class="background-fix vuetable-pagination d-flex mb-3">
      <vuetable-pagination-info
        ref="paginationInfo"
        class="flex-fill"
        info-class="pagination-info"
        info-template="{from} to {to} of {total} items" />

      <vuetable-pagination-bootstrap
        ref="pagination"
        :css="css.pagination"
        class="flex-fill text-right"
        @vuetable-pagination:change-page="onChangePage" />
    </div>

    <campaign-create-modal />

  </div>
</div>
</div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex';
  import BreadcrumbsManager from '../../helpers/BreadcrumbManager';
  import CampaignCreateModal from '../../components/CampaignCreateModal';
  import FilterBar from '../../components/FilterBar';
  import Vuetable from 'vuetable-2';
  import VuetablePaginationBootstrap from '../../components/VuetablePaginationBootstrap.vue';
  import VuetablePaginationInfo from 'vuetable-2/src/components/VuetablePaginationInfo';

  export default {      
    mixins: [BreadcrumbsManager],

    components: {
      CampaignCreateModal,
      FilterBar,
      Vuetable,
      VuetablePaginationBootstrap,
      VuetablePaginationInfo,
    },

    data() {
      return {
        //api_url: '/api/physicians/physicians_paged',
        emptyText: 'Loading...please wait',

        fields: [           
          {
            name: 'last_name',
            title: 'Name',
            sortField: 'last_name',
          },
          { 
            name: 'location_primary',
            title: 'Location',
            titleClass: 'hidden_mobile',
            dataClass: 'hidden_mobile',
          },
          {
            name: 'last_checkin_id',
            title: 'Last Check-in',
            sortField: 'last_checkin_id',          
          },          
          {
            name: 'referrer_score',
            title: 'Score',
            sortField: 'referrer_score'
          },
          {
            name: 'created_at',
            title: 'Added',
            sortField: 'created_at',
            titleClass: 'd-none d-md-table-cell',
            dataClass: 'd-none d-md-table-cell',
          },          
          {
            name: 'actions',
            title: '',
          },
        ],

        css: {
          table: {
            tableClass: 'table physicians-table',
            ascendingIcon: 'fas fa-sort-alpha-down',
            descendingIcon: 'fas fa-sort-alpha-up'
          },

          pagination: {
            wrapperClass: 'pagination',
            activeClass: 'active',
            disabledClass: 'disabled',
            pageClass: 'page',
            linkClass: 'link',
            icons: {
              first: '',
              prev: '',
              next: '',
              last: '',
            },
          },

          icons: {
            first: 'fas fa-angle-double-left',
            prev: 'fas fa-angle-left',
            next: 'fas fa-angle-right',
            last: 'fas fa-angle-double-right',
          },
        },

        sortOrder: [
          {
            field: 'last_name',
            direction: 'asc',
          }
        ],

        moreParams: {
        },

        checkin_options: {},

        selectedPhysicians: [],

        showPhysicianGroups: false,
      };
    },

    created() {
    },

    activated() {
      this.setBreadcrumbs([
          { text: 'Home', to: { name: 'home' } },
          { text: 'Physicians', to: { name: 'physicians.list' } },
      ]); 
      this.$refs.vuetable.refresh();
    },

    mounted() {
      this.$root.$on('providers_updated', data => {
        this.$refs.vuetable.refresh();
      });
    },

    methods: {
      buildCheckinModal(physician_id, display_name) {
        this.checkin_options.checkinables = {
          display_name : display_name,
          morphable_id : physician_id,
          morphable_type : 'App\\Physician'
        };

        this.$root.$emit('build_checkin_modal', { checkin_options: this.checkin_options });
      },

      getSortParam: function(sortOrder) {
        return sortOrder.map(function(sort) {
          return (sort.direction === 'desc' ? '-' : '') + sort.field
        }).join(',')
      },

      onPaginationData (paginationData) {
        this.$refs.pagination.setPaginationData(paginationData)
        this.$refs.paginationInfo.setPaginationData(paginationData)
      },

      onChangePage (page) {
        this.$refs.vuetable.changePage(page)
      },

      selectGroupFilter(filterGroupId) {
        this.showPhysicianGroups = filterGroupId.length;

        this.moreParams = {
          'filter[has_groups]': filterGroupId
        }
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },

      selectSpecialtyFilter(filterSpecialtyId) {
        this.moreParams = {
          'filter[has_specialties]': filterSpecialtyId
        }
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },

      setFilter(filterTextName) {
        this.moreParams = {
          'filter[search_full_name]': filterTextName
        }
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },
      
      resetAllFilters() {
        this.moreParams = {}
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },

      onDataLoaded() {
        Vue.nextTick( () => {
          if (this.$refs.vuetable.tableData.length == 0) {
            this.emptyText = 'No records found';
          }
        });          
      },

      setCampaignVisits() {
        this.$root.$emit('build_campaign_modal', { 
          campaign_stops: this.allPhysicianIds.filter(provider => this.selectedPhysicians.includes(provider.morphable_id)),
          save_physicians_first: false
        });
      }, 

      setRouteVisits() {
        this.$root.$emit('build_route_modal', { 
          route_stops: this.routeStops,
          save_physicians_first: false
        });
      }, 
    },

    computed: {
      ...mapGetters(['allPhysicianIds', 'mappablePhysicianIds']),
      ...mapState(['perms']),

      routeStops() {
        return this.mappablePhysicianIds.filter(provider => this.selectedPhysicians.includes(provider.morphable_id));
      }
    },
  }
</script>
<style scoped>
tr:hover .drop-down { 
    opacity:1;
    transition:.2s all ease-out;
  }
</style>